import axios, { AxiosError } from "axios";
import {
  Prompt,
  PromptRequest,
  JsonSchema,
  JsonSchemaRequest,
  Assertion,
  AssertionRequest,
  TestVariable,
  TestVariableRequest,
  Provider,
  ProviderRequest,
  Test,
  TestRequest,
  Evaluation,
  EvaluationRequest,
  DashboardData,
  ApiError,
  Dataset,
  DatasetRequest,
  DatasetVariable,
  DatasetVariableRequest,
  ApiResponse,
  VariableGroup,
  DatasetVariableWithName,
  CombinedEvaluationRequest,
} from "../types";

// Create an axios instance with default config
const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Response interceptor for handling errors globally
instance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const response = error.response;

    // Log errors for debugging
    console.error("API Error:", response?.data || error.message);

    // Customize error message based on response
    let errorMessage = "Something went wrong. Please try again.";

    if (response?.status === 401) {
      errorMessage = "Your session has expired. Please log in again.";
      // Don't redirect automatically - this causes loops
      // Instead, let the component handle the error
    } else if (response?.status === 403) {
      errorMessage = "You do not have permission to perform this action.";
    } else if (response?.status === 404) {
      errorMessage = "The requested resource was not found.";
    } else if (response?.data && (response.data as any).message) {
      errorMessage = (response.data as any).message;
    }

    // Throw the error with the customized message
    return Promise.reject({
      ...error,
      customMessage: errorMessage,
      isAuthError: response?.status === 401,
    } as ApiError);
  },
);

// Prompt endpoints
const prompts = {
  getAll: () => instance.get<Prompt[]>("/prompts"),
  getById: (id: string) => instance.get<Prompt>(`/prompts/${id}`),
  create: (data: PromptRequest) => instance.post<Prompt>("/prompts", data),
  update: (id: string, data: Partial<PromptRequest>) =>
    instance.put<Prompt>(`/prompts/${id}`, data),
  delete: (id: string) =>
    instance.delete<{ message: string }>(`/prompts/${id}`),
};

// JSON Schema endpoints
const schemas = {
  getAll: () => instance.get<JsonSchema[]>("/schemas"),
  getById: (id: string) => instance.get<JsonSchema>(`/schemas/${id}`),
  create: (data: JsonSchemaRequest) =>
    instance.post<JsonSchema>("/schemas", data),
  update: (id: string, data: Partial<JsonSchemaRequest>) =>
    instance.put<JsonSchema>(`/schemas/${id}`, data),
  delete: (id: string) =>
    instance.delete<{ message: string }>(`/schemas/${id}`),
};

// Assertion endpoints
const assertions = {
  getAll: () => instance.get<Assertion[]>("/assertions"),
  getById: (id: string) => instance.get<Assertion>(`/assertions/${id}`),
  create: (data: AssertionRequest) =>
    instance.post<Assertion>("/assertions", data),
  update: (id: string, data: Partial<AssertionRequest>) =>
    instance.put<Assertion>(`/assertions/${id}`, data),
  delete: (id: string) =>
    instance.delete<{ message: string }>(`/assertions/${id}`),
};

// Test Variable endpoints
const variables = {
  getAll: () => instance.get<TestVariable[]>("/variables"),
  getById: (id: string) => instance.get<TestVariable>(`/variables/${id}`),
  create: (data: TestVariableRequest) =>
    instance.post<TestVariable>("/variables", data),
  update: (id: string, data: Partial<TestVariableRequest>) =>
    instance.put<TestVariable>(`/variables/${id}`, data),
  delete: (id: string) =>
    instance.delete<{ message: string }>(`/variables/${id}`),
};

// Provider endpoints
const providers = {
  getAll: () => instance.get<Provider[]>("/providers"),
  getById: (id: string) => instance.get<Provider>(`/providers/${id}`),
  create: (data: ProviderRequest) =>
    instance.post<Provider>("/providers", data),
  update: (id: string, data: Partial<ProviderRequest>) =>
    instance.put<Provider>(`/providers/${id}`, data),
  delete: (id: string) =>
    instance.delete<{ message: string }>(`/providers/${id}`),
};

// Test endpoints
const tests = {
  getAll: () => instance.get<Test[]>("/tests"),
  getById: (id: string) => instance.get<Test>(`/tests/${id}`),
  create: (data: TestRequest) => instance.post<Test>("/tests", data),
  update: (id: string, data: Partial<TestRequest>) =>
    instance.put<Test>(`/tests/${id}`, data),
  delete: (id: string) => instance.delete<{ message: string }>(`/tests/${id}`),
};

// Evaluation endpoints
const evaluations = {
  getAll: () => instance.get<Evaluation[]>("/evaluations"),
  getById: (id: string) => instance.get<Evaluation>(`/evaluations/${id}`),
  create: (data: EvaluationRequest) =>
    instance.post<Evaluation>("/evaluations", data),
  createCombined: (data: CombinedEvaluationRequest) =>
    instance.post<Evaluation>("/evaluations/combined", data),
  update: (id: string, data: Partial<EvaluationRequest>) =>
    instance.put<Evaluation>(`/evaluations/${id}`, data),
  abort: (id: string) =>
    instance.post<{ message: string }>(`/evaluations/${id}/abort`),
  retry: (id: string) => instance.post<Evaluation>(`/evaluations/${id}/retry`),
  delete: (id: string) =>
    instance.delete<{ message: string }>(`/evaluations/${id}`),
};

// Dashboard endpoint
const dashboard = {
  getSummary: () => instance.get<DashboardData>("/dashboard/summary"),
};

// Auth endpoint (mock)
const auth = {
  login: (email: string, password: string) => {
    // Mock implementation - in a real app, this would call the backend
    return Promise.resolve({
      data: {
        token: "mock-jwt-token",
        user: {
          id: "1",
          name: "Test User",
          email: email,
          role: "admin",
        },
      },
    });
  },
};

// Dataset API
const datasets = {
  getAll: (): Promise<ApiResponse<Dataset[]>> => {
    return instance.get("/prompt-datasets");
  },
  getById: (id: string): Promise<ApiResponse<Dataset>> => {
    return instance.get(`/prompt-datasets/${id}`);
  },
  create: (data: DatasetRequest): Promise<ApiResponse<Dataset>> => {
    return instance.post("/prompt-datasets", data);
  },
  update: (id: string, data: DatasetRequest): Promise<ApiResponse<Dataset>> => {
    return instance.put(`/prompt-datasets/${id}`, data);
  },
  delete: (id: string): Promise<ApiResponse<void>> => {
    return instance.delete(`/prompt-datasets/${id}`);
  },
  linkPrompt: (id: string, promptId: string): Promise<ApiResponse<Dataset>> => {
    return instance.put(`/prompt-datasets/${id}/link-prompt`, { promptId });
  },
  unlinkPrompt: (id: string): Promise<ApiResponse<Dataset>> => {
    return instance.put(`/prompt-datasets/${id}/unlink-prompt`);
  },
  updateVariables: (
    id: string,
    variables: Record<string, DatasetVariable[]>,
  ): Promise<ApiResponse<Dataset>> => {
    return instance.put(`/prompt-datasets/${id}/variables`, { variables });
  },
};

// Dataset Variables API
const datasetVariables = {
  create: (
    datasetId: string,
    variableType: string,
    data: DatasetVariableRequest,
  ): Promise<ApiResponse<DatasetVariable>> => {
    return instance.post(
      `/prompt-datasets/${datasetId}/variables/${variableType}`,
      data,
    );
  },
  update: (
    datasetId: string,
    variableType: string,
    variableId: string,
    data: DatasetVariableRequest,
  ): Promise<ApiResponse<DatasetVariable>> => {
    return instance.put(
      `/prompt-datasets/${datasetId}/variables/${variableType}/${variableId}`,
      data,
    );
  },
  delete: (
    datasetId: string,
    variableType: string,
    variableId: string,
  ): Promise<ApiResponse<void>> => {
    return instance.delete(
      `/prompt-datasets/${datasetId}/variables/${variableType}/${variableId}`,
    );
  },
};

// Variable Groups API
const variableGroups = {
  getAll: (datasetId: string): Promise<ApiResponse<VariableGroup[]>> => {
    return instance.get(`/prompt-datasets/${datasetId}/variable-groups`);
  },
  getById: (
    datasetId: string,
    groupId: string,
  ): Promise<ApiResponse<VariableGroup>> => {
    return instance.get(
      `/prompt-datasets/${datasetId}/variable-groups/${groupId}`,
    );
  },
  create: (
    datasetId: string,
    data: { name: string; description?: string; templateVariables?: string[] },
  ): Promise<ApiResponse<VariableGroup>> => {
    return instance.post(`/prompt-datasets/${datasetId}/variable-groups`, data);
  },
  update: (
    datasetId: string,
    groupId: string,
    data: {
      name: string;
      description?: string;
      variables?: Record<string, any>;
    },
  ): Promise<ApiResponse<VariableGroup>> => {
    return instance.put(
      `/prompt-datasets/${datasetId}/variable-groups/${groupId}`,
      data,
    );
  },
  delete: (datasetId: string, groupId: string): Promise<ApiResponse<void>> => {
    return instance.delete(
      `/prompt-datasets/${datasetId}/variable-groups/${groupId}`,
    );
  },
  createVariable: (
    datasetId: string,
    groupId: string,
    data: DatasetVariableRequest,
  ): Promise<ApiResponse<DatasetVariableWithName>> => {
    return instance.post(
      `/prompt-datasets/${datasetId}/variable-groups/${groupId}/variables`,
      data,
    );
  },
  updateVariable: (
    datasetId: string,
    groupId: string,
    variableId: string,
    data: DatasetVariableRequest,
  ): Promise<ApiResponse<DatasetVariableWithName>> => {
    return instance.put(
      `/prompt-datasets/${datasetId}/variable-groups/${groupId}/variables/${variableId}`,
      data,
    );
  },
  deleteVariable: (
    datasetId: string,
    groupId: string,
    variableId: string,
  ): Promise<ApiResponse<void>> => {
    return instance.delete(
      `/prompt-datasets/${datasetId}/variable-groups/${groupId}/variables/${variableId}`,
    );
  },
  addAssertion: (
    datasetId: string,
    groupId: string,
    assertionId: string,
  ): Promise<ApiResponse<VariableGroup>> => {
    return instance.post(
      `/prompt-datasets/${datasetId}/variable-groups/${groupId}/assertions`,
      { assertionId },
    );
  },
  addAssertionDirectly: (
    datasetId: string,
    groupId: string,
    assertionData: AssertionRequest,
  ): Promise<ApiResponse<VariableGroup>> => {
    return instance.post(
      `/prompt-datasets/${datasetId}/variable-groups/${groupId}/assertions/direct`,
      assertionData,
    );
  },
  getAssertions: (
    datasetId: string,
    groupId: string,
  ): Promise<ApiResponse<Assertion[]>> => {
    return instance.get(
      `/prompt-datasets/${datasetId}/variable-groups/${groupId}/assertions`,
    );
  },
  removeAssertion: (
    datasetId: string,
    groupId: string,
    assertionId: string,
  ): Promise<ApiResponse<VariableGroup>> => {
    return instance.delete(
      `/prompt-datasets/${datasetId}/variable-groups/${groupId}/assertions/${assertionId}`,
    );
  },
};

// Export the APIs
const api = {
  auth,
  prompts,
  schemas,
  assertions,
  variables,
  tests,
  evaluations,
  providers,
  dashboard,
  datasets,
  datasetVariables,
  variableGroups,
};

// Export default
export default api;

export { instance };
